/**
 * Copyright (c) 2024 Spazious, Inc All Rights Reserved
 * Proprietary and confidential
 *
 * Written by Rubeniskov <ruben.lopez@spazious.com>, Thu Feb 15 2024
 */

import { Fiber, HostConfig as ReactHostConfig } from 'react-reconciler';
import { DefaultEventPriority } from 'react-reconciler/constants.js';

import { GraphNodeCtor } from '@spazious/dollhouse-graph';
import { hasOwnProperty } from '@spazious/dollhouse-utils';

import { Container, Instance, Props, PublicInstance, Type } from '../types.js';

import { applyProps, catalogue, getComponentType, precacheFiberNode } from './fiber.js';

export type MeasureOnSuccessCallback = (
  x: number,
  y: number,
  width: number,
  height: number,
  pageX: number,
  pageY: number
) => void;

type InspectorDataProps = {
  [propName: string]: string;
};

type InspectorDataSource = {
  fileName?: string;
  lineNumber?: number;
};

interface InspectorDataGetter {
  (): {
    measure: (callback: MeasureOnSuccessCallback) => void;
    props: InspectorDataProps;
    source: InspectorDataSource;
  };
}

export type InspectorData = {
  closestInstance?: any;
  hierarchy: Array<{
    name: string | null | void;
    getInspectorData: InspectorDataGetter;
  }>;
  selectedIndex: number | null | void;
  props: InspectorDataProps;
  source: InspectorDataSource | null | void;
};

export type TouchedViewDataAtPoint = {
  pointerY: number;
  touchedViewTag?: number;
  frame: {
    top: number;
    left: number;
    width: number;
    height: number;
  };
} & InspectorData;

export type RendererInspectionConfig = {
  getInspectorDataForInstance?: (instance: Fiber | null) => InspectorData;
  // Deprecated. Replaced with getInspectorDataForViewAtPoint.
  getInspectorDataForViewTag?: (tag: number) => Object;
  getInspectorDataForViewAtPoint?: (
    inspectedView: Object,
    locationX: number,
    locationY: number,
    callback: (viewData: TouchedViewDataAtPoint) => any
  ) => void;
};

export type GraphHostConfig = ReactHostConfig<
  Type,
  Props,
  Container,
  Instance,
  void,
  Instance,
  Instance,
  PublicInstance,
  never,
  true | null,
  never,
  number | undefined,
  -1
>;

// Don't handle text instances, warn on undefined behavior
const handleTextWarning = () => {
  throw new Error(
    'Text is not allowed in the GraphTree! This could be stray whitespace or characters.'
  );
};

const appendChild = (parentInstance: Instance, child: Instance): void => {
  parentInstance.appendChild(child);
};

const insertBefore = (parentInstance: Instance, child: Instance, beforeChild: Instance): void => {
  parentInstance.insertBefore(child, beforeChild);
};

const removeChild = (parentInstance: Instance, child: Instance): void => {
  parentInstance.removeChild(child);
};

export const HostConfig: GraphHostConfig = {
  supportsHydration: true,
  supportsPersistence: false,
  supportsMutation: true,
  isPrimaryRenderer: false,
  warnsIfNotActing: true,
  noTimeout: -1,
  scheduleTimeout: setTimeout,
  cancelTimeout: clearTimeout,
  getInstanceFromNode: () => {
    return null;
  },
  beforeActiveInstanceBlur: () => {
    // NOOP
  },
  afterActiveInstanceBlur: () => {
    // NOOP
  },
  prepareScopeUpdate: () => {
    // NOOP
  },
  getInstanceFromScope: () => {
    return null;
  },
  createInstance: (type, props, rootContainer, _, internalHandle) => {
    if (!hasOwnProperty(catalogue, type)) {
      throw new Error(
        `Unknown Graph component type "${type}" did you forget to register it using extend({ "${type}": GraphNode })?`
      );
    }

    // TODO add propper typings
    const Target = getComponentType(type) as any as GraphNodeCtor;

    const instance = new Target(type);
    // This will help the node to find the root node
    // instead of traversing the tree up inside the node logic
    instance.evaluateRoot = false;
    instance.root = rootContainer;

    applyProps(instance, props);
    (instance as any).applyProps?.(props);
    precacheFiberNode(instance, internalHandle);
    return instance;
  },
  createTextInstance: handleTextWarning,
  hideTextInstance: handleTextWarning,
  unhideTextInstance: handleTextWarning,

  getPublicInstance: (instance: Instance) => {
    return instance;
  },

  appendInitialChild: appendChild,
  appendChild,
  appendChildToContainer: appendChild,

  insertBefore,
  insertInContainerBefore: insertBefore,

  removeChild,
  removeChildFromContainer: removeChild,

  finalizeInitialChildren: () => {
    return false;
  },
  prepareUpdate: () => {
    // TODO: This is a noop for now, but we should be able to use this to
    // determine if a component needs to be updated or not.
    return true;
  },
  prepareForCommit: () => {
    return null;
  },
  commitUpdate: (instance, updatePayload, type, _oldProps, newProps, fiber): void => {
    applyProps(instance, newProps);
    (instance as any).applyProps?.(newProps);
  },
  commitMount: () => {
    // In case we need to do something when a node is mounted and finalizeInitialChildren returns true
    // console.log('commitMount', instance, _type, _props, _int);
  },
  hideInstance: () => {
    // console.log('hideInstance');
    // console.log(instance);
  },
  unhideInstance() {
    // console.log('unhideInstance');
    // console.log(instance, props);
  },
  resetAfterCommit: (containerInfo) => {
    containerInfo.emit('update');
  },
  detachDeletedInstance: (node) => {
    node.clean();
  },
  clearContainer: (container) => {
    container.clean();
  },
  preparePortalMount: () => {
    // Noop
  },
  shouldSetTextContent: () => {
    return false;
  },
  getRootHostContext: () => {
    return null;
  },
  getChildHostContext: () => {
    return undefined as never;
  },
  getCurrentEventPriority: () => {
    return DefaultEventPriority;
  },
  // // @ts-ignore
  // maySuspendCommit: () => {
  //   return false;
  // },
  // // @ts-ignore
  // preloadInstance: () => {
  //   console.log('preloadInstance');
  //   // Noop
  // },
  // // @ts-ignore
  // startSuspendingCommit: () => {
  //   console.log('startSuspendingCommit');
  //   // Noop
  // },
  // // @ts-ignore
  // suspendInstance: () => {
  //   console.log('suspendInstance');
  //   // Noop
  //   return null;
  // },
  // // @ts-ignore
  // // https://github.com/facebook/react/blob/dc3178151b8cb0359e5c36b8ed57458b807ae8e3/packages/react-reconciler/src/forks/ReactFiberConfig.custom.js#L77C49-L77C71
  // waitForCommitToBeReady: () => {
  //   console.log('waitForCommitToBeReady');
  // },
  // NotPendingTransition: 0,
};

// // Counter for uniquely identifying views.
// // % 10 === 1 means it is a rootTag.
// // % 2 === 0 means it is a Fabric tag.
// let nextReactTag = 3;
// function allocateTag() {
//   let tag = nextReactTag;
//   if (tag % 10 === 1) {
//     tag += 2;
//   }
//   nextReactTag = tag + 2;
//   return tag;
// }

// function recursivelyUncacheFiberNode(node: Instance) {
//   if (typeof node === 'number') {
//     // Leaf node (eg text)
//     uncacheFiberNode(node);
//   } else {
//     uncacheFiberNode((node as any)._nativeTag);

//     (node as any)._children.forEach(recursivelyUncacheFiberNode);
//   }
// }
